import { useEffect, useState } from "react";
import { get } from "./api";
import { useTenant } from "./tenant";

/* ─────────────────────────────────────────────────────────────
   Per-area branding
   ─────────────────────────────────────────────────────────────
   One app serves every area. The accent, title, favicon and
   theme-color follow the active area (nav/StaffShell) or, outside
   the staff shell, the public page (app/guards Branded) via
   `setBrandSideEffects(BRANDS[...])`.

   Per-tenant overrides (custom logo + colours uploaded by
   admins) still live in `useBranding()` below — that hook
   layers tenant colours on top of the product accent. */

export type ProductBrand = "control" | "legionella" | "tracker" | "hr";

export interface BrandConfig {
  product: ProductBrand;
  /** Short display name — used in the sidebar. */
  name: string;
  /** Full marketing name — used in <title>. */
  fullName: string;
  /** Short tagline shown below the logo in the sidebar. */
  tagline: string;
  /** CSS custom property that holds the product accent colour. */
  accentVar: string;
  /** Literal accent colour used to populate the CSS var + theme-color meta. */
  accentColor: string;
  /** Home path of this app on the (single) site, e.g. "/legionella". */
  homeUrl: string;
  /** Path to the SVG logo served from /public/logos/. */
  logoPath: string;
}

export const BRANDS: Record<ProductBrand, BrandConfig> = {
  control: {
    product: "control",
    name: "Control",
    fullName: "Control Platform",
    tagline: "Multi-product compliance + tracking suite",
    accentVar: "--accent-control",
    accentColor: "#475569", // slate-600
    homeUrl: "/",
    logoPath: "/logos/control.svg",
  },
  legionella: {
    product: "legionella",
    name: "Legionella",
    fullName: "Control Legionella",
    tagline: "Water-compliance field operations",
    accentVar: "--accent-legionella",
    accentColor: "#0d9488", // teal-600
    homeUrl: "/legionella",
    logoPath: "/logos/legionella.svg",
  },
  tracker: {
    product: "tracker",
    name: "Tracker",
    fullName: "Control Tracker",
    tagline: "Fleet + asset tracking",
    accentVar: "--accent-tracker",
    accentColor: "#ea580c", // orange-600
    homeUrl: "/vehicles",
    logoPath: "/logos/tracker.svg",
  },
  hr: {
    product: "hr",
    name: "HR",
    fullName: "Control HR",
    tagline: "People management & payroll",
    accentVar: "--accent-hr",
    accentColor: "#7c3aed", // violet-600
    homeUrl: "/hr",
    logoPath: "/logos/hr.svg",
  },
};

/** Every page is in the one app now; only absolute URLs are external. */
export function productHref(path: string): { href: string; external: boolean } {
  return { href: path, external: /^https?:\/\//.test(path) };
}

/**
 * Applies the product brand to the browsing chrome:
 *
 *   • sets `<title>` to the product's full name (existing
 *     screens can append context with `document.title = ...`
 *     and the next route change still reads the brand from us);
 *   • sets/creates the `<meta name="theme-color">` tag so PWAs
 *     and mobile address bars pick the brand accent;
 *   • swaps the favicon (`<link rel="icon">` + the matching
 *     `apple-touch-icon`) to the per-product SVG;
 *   • writes the brand accent into `--accent-control` /
 *     `--accent-legionella` / `--accent-tracker` AND mirrors it
 *     into a generic `--accent-product` token that page-level
 *     CSS can reference without caring which bundle is live.
 *
 * Safe to call multiple times. Idempotent.
 */
export function setBrandSideEffects(brand: BrandConfig): void {
  if (typeof document === "undefined") return;

  // Title
  document.title = brand.fullName;

  // theme-color meta
  let themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!themeMeta) {
    themeMeta = document.createElement("meta");
    themeMeta.name = "theme-color";
    document.head.appendChild(themeMeta);
  }
  themeMeta.content = brand.accentColor;

  // Favicon swap — first try to update an existing tag; otherwise create one.
  const setIcon = (rel: string) => {
    let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.type = "image/svg+xml";
    link.href = brand.logoPath;
  };
  setIcon("icon");
  setIcon("apple-touch-icon");

  // CSS custom properties — product-specific name + generic name.
  const root = document.documentElement;
  root.style.setProperty(brand.accentVar, brand.accentColor);
  root.style.setProperty("--accent-product", brand.accentColor);
  root.dataset.product = brand.product;
}

/* ─────────────────────────────────────────────────────────────
   Per-tenant branding overrides (existing functionality)
   ───────────────────────────────────────────────────────────── */

interface BrandingPayload {
  logo_url: string | null;
  primary_color: string | null;
  accent_color: string | null;
}

/**
 * Phase B.1 — fetches the active tenant's branding and applies it
 * by writing the colours into CSS custom properties on `<html>`. The
 * logo URL is returned to the caller so the top-bar can swap.
 *
 * Cached per-session (the hook just refetches when the active tenant
 * changes). Admin updates flush the server-side cache; the SPA
 * picks up the change on the next mount.
 */
const DEFAULT_BRANDING: BrandingPayload = {
  logo_url: null, primary_color: null, accent_color: null,
};

export function useBranding(): BrandingPayload {
  const { current } = useTenant();
  const [b, setB] = useState<BrandingPayload>(DEFAULT_BRANDING);

  useEffect(() => {
    if (!current?.id) return;
    let cancelled = false;
    get<{ data?: BrandingPayload } | BrandingPayload | null>(`/v1/branding/${current.id}`)
      .then((r) => {
        if (cancelled) return;
        // Be defensive about response shape. The endpoint returns
        // {data: BrandingPayload}, but a stale frontend nginx or a
        // hijacked route can deliver HTML / null / a string / an
        // already-unwrapped payload. Any of those used to land in
        // setB() and crash the next render reading t.primary_color.
        let payload: BrandingPayload | null = null;
        if (r && typeof r === "object") {
          if ("data" in r && r.data && typeof r.data === "object") {
            payload = r.data as BrandingPayload;
          } else if ("primary_color" in r || "accent_color" in r || "logo_url" in r) {
            payload = r as BrandingPayload;
          }
        }
        setB({
          logo_url:      payload?.logo_url      ?? null,
          primary_color: payload?.primary_color ?? null,
          accent_color:  payload?.accent_color  ?? null,
        });
      })
      .catch(() => { /* fall back to defaults; state is already safe */ });
    return () => { cancelled = true; };
  }, [current?.id]);

  // Apply colours to CSS custom properties so the rest of the app
  // picks them up via existing var() usages. Reverts on unmount.
  useEffect(() => {
    const root = document.documentElement;
    const prevPrimary = root.style.getPropertyValue("--brand-primary");
    const prevAccent  = root.style.getPropertyValue("--brand-accent");
    if (b.primary_color) root.style.setProperty("--brand-primary", b.primary_color);
    if (b.accent_color)  root.style.setProperty("--brand-accent",  b.accent_color);
    return () => {
      root.style.setProperty("--brand-primary", prevPrimary);
      root.style.setProperty("--brand-accent",  prevAccent);
    };
  }, [b.primary_color, b.accent_color]);

  return b;
}
